import type { ProcessingTypeKey } from '../../../Generated/ProcessingType.ts'
import { ProcessingTypes } from '../../../Generated/ProcessingType.ts'
import { taxaOptions } from '../HomePage/01-CreateOrderPage/TaxaOptions.tsx'

/**
 * The order list's filters, search and paging as they appear in the URL query string.
 *
 * | Param        | State            | Values                                                  | Default (omitted) |
 * |--------------|------------------|---------------------------------------------------------|-------------------|
 * | `q`          | `search`         | free text, matched against name and description         | `''`              |
 * | `completed`  | `onlyCompleted`  | `1`                                                     | off               |
 * | `submitted`  | `onlySubmitted`  | `1`                                                     | off               |
 * | `taxon`      | `taxonomy`       | a target taxonomy key, or `none` for orders without one | all taxa          |
 * | `processing` | `processingType` | `METABARCODING`, `EDNA`                                 | all types         |
 * | `org`        | `orgId`          | an organization UUID (lab or customer)                  | all organizations |
 * | `page`       | `page`           | 1-based page number                                     | `1`               |
 * | `size`       | `size`           | one of `pageSizes`                                      | `10`              |
 *
 * Values that don't parse fall back to the default.
 */
export type OrderListUrlState = {
  search: string
  onlyCompleted: boolean
  onlySubmitted: boolean
  /** `''` selects orders whose target taxonomy is unset. */
  taxonomy: string | null
  processingType: ProcessingTypeKey | null
  orgId: string | null
  page: number
  size: number
}

export const pageSizes = [10, 100, 250, 500] as const

export const defaultOrderListUrlState: OrderListUrlState = {
  search: '',
  onlyCompleted: false,
  onlySubmitted: false,
  taxonomy: null,
  processingType: null,
  orgId: null,
  page: 1,
  size: 10,
}

const PARAM = {
  search: 'q',
  onlyCompleted: 'completed',
  onlySubmitted: 'submitted',
  taxonomy: 'taxon',
  processingType: 'processing',
  orgId: 'org',
  page: 'page',
  size: 'size',
} as const

const UNSET_TAXON = 'none'
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

const parseFlag = (raw: string | null): boolean => raw === '1' || raw === 'true'

const parsePositiveInt = (raw: string | null): number | null => {
  if (raw === null || !/^\d+$/.test(raw)) {
    return null
  }
  const n = Number(raw)
  return Number.isSafeInteger(n) && n >= 1 ? n : null
}

const parseTaxonomy = (raw: string | null): string | null => {
  if (raw === UNSET_TAXON) {
    return ''
  }
  return taxaOptions.some((o) => o.value === raw) ? raw : null
}

const parseProcessingType = (raw: string | null): ProcessingTypeKey | null =>
  raw !== null && Object.hasOwn(ProcessingTypes, raw) ? (raw as ProcessingTypeKey) : null

export function parseOrderListUrlState(params: URLSearchParams): OrderListUrlState {
  const size = parsePositiveInt(params.get(PARAM.size))
  const org = params.get(PARAM.orgId)
  return {
    search: params.get(PARAM.search) ?? defaultOrderListUrlState.search,
    onlyCompleted: parseFlag(params.get(PARAM.onlyCompleted)),
    onlySubmitted: parseFlag(params.get(PARAM.onlySubmitted)),
    taxonomy: parseTaxonomy(params.get(PARAM.taxonomy)),
    processingType: parseProcessingType(params.get(PARAM.processingType)),
    orgId: org !== null && UUID.test(org) ? org : null,
    page: parsePositiveInt(params.get(PARAM.page)) ?? defaultOrderListUrlState.page,
    size: size !== null && (pageSizes as readonly number[]).includes(size) ? size : defaultOrderListUrlState.size,
  }
}

export function serializeOrderListUrlState(state: OrderListUrlState): URLSearchParams {
  const params = new URLSearchParams()
  if (state.search !== '') {
    params.set(PARAM.search, state.search)
  }
  if (state.onlyCompleted) {
    params.set(PARAM.onlyCompleted, '1')
  }
  if (state.onlySubmitted) {
    params.set(PARAM.onlySubmitted, '1')
  }
  if (state.taxonomy !== null) {
    params.set(PARAM.taxonomy, state.taxonomy === '' ? UNSET_TAXON : state.taxonomy)
  }
  if (state.processingType !== null) {
    params.set(PARAM.processingType, state.processingType)
  }
  if (state.orgId !== null) {
    params.set(PARAM.orgId, state.orgId)
  }
  if (state.page !== defaultOrderListUrlState.page) {
    params.set(PARAM.page, String(state.page))
  }
  if (state.size !== defaultOrderListUrlState.size) {
    params.set(PARAM.size, String(state.size))
  }
  return params
}
